import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * Recommends up to three full size products related to what is already in the cart,
 * and adds one without a page reload.
 *
 * The list comes from /recommendations/products.json seeded by the first non-sample
 * cart item. Sample sachets and anything already in the cart are filtered out, so this
 * row never duplicates the sample suggestion row that sits above it.
 *
 * Rendered hidden and revealed only once a usable list is in place, so with JavaScript
 * off, or when nothing sensible comes back, the customer sees nothing rather than an
 * empty heading.
 *
 * @typedef {object} CartRecommendationsRefs
 * @property {HTMLElement} [list] - The list to populate.
 * @property {HTMLElement} [status] - The live region used for feedback.
 * @extends Component<CartRecommendationsRefs>
 */
class CartRecommendations extends Component {
  /** @type {MutationObserver | null} */
  #hiddenObserver = null;

  /** @type {boolean} Guards against overlapping loads. */
  #loading = false;

  connectedCallback() {
    super.connectedCallback();

    this.#reload();

    /*
      Same morph problem as the sample suggestions row: morphSection reuses this node
      and re-applies the `hidden` it ships with, without re-running connectedCallback.
      It also replaces the list with the server's empty <ul>, so there is nothing left
      to reveal, which is why this rebuilds rather than just unhiding.

      cart:update is dispatched before morphSection, so reacting to that event would be
      undone by the morph that follows. Watch the attribute instead.
    */
    this.#hiddenObserver = new MutationObserver(() => this.#onRehidden());
    this.#hiddenObserver.observe(this, { attributes: true, attributeFilter: ['hidden'] });
  }

  disconnectedCallback() {
    this.#hiddenObserver?.disconnect();
    this.#hiddenObserver = null;
    super.disconnectedCallback?.();
  }

  #onRehidden() {
    if (!this.hidden) return;

    const { list } = this.refs;
    // Rows survived the morph, so just show them again.
    if (list?.children.length) {
      this.hidden = false;
      return;
    }
    // The morph replaced the list with the server's empty one, so fetch again.
    this.#reload();
  }

  async #reload() {
    if (this.#loading) return;
    this.#loading = true;
    try {
      await this.#load();
    } finally {
      this.#loading = false;
    }
  }

  /** Fetches and renders the recommendations. */
  async #load() {
    const { list } = this.refs;
    const seed = this.dataset.seedId;
    if (!list || !seed) return;

    const excluded = new Set(
      (this.dataset.inCart || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );

    try {
      const limit = this.dataset.limit || '6';
      const intent = this.dataset.intent || 'related';
      /*
        Theme.routes.product_recommendations_url is /recommendations/products, which
        serves a rendered SECTION and answers 422 to a bare query like this one. The
        JSON payload this component parses lives at the .json variant, so ask for that
        explicitly. Without it every fetch 422'd, response.ok was false, #load returned
        early and the row stayed hidden, on the cart page as well as in the drawer.
      */
      const base = Theme.routes.product_recommendations_url.replace(/\/$/, '');
      const endpoint = base.endsWith('.json') ? base : `${base}.json`;
      const url = `${endpoint}?product_id=${encodeURIComponent(
        seed
      )}&limit=${encodeURIComponent(limit)}&intent=${encodeURIComponent(intent)}`;

      const response = await fetch(url);
      if (!response.ok) return;

      const data = await response.json();
      const products = (data.products || [])
        .filter((product) => product.type !== 'Tea Sample')
        .filter((product) => !excluded.has(String(product.id)))
        .filter((product) => product.available !== false)
        // Display count, set by the host: 3 on the cart page, 2 in the drawer.
        .slice(0, Number(this.dataset.displayLimit) || 3);

      if (!products.length) return;

      // Rebuild rather than append: this can run again after a morph.
      list.replaceChildren();
      for (const product of products) {
        list.appendChild(this.#row(product));
      }

      // Only reveal once there is something worth showing.
      this.hidden = false;
    } catch (error) {
      // Leave the row hidden; a failed recommendation call is not worth surfacing.
    }
  }

  /**
   * Builds one recommendation row.
   * @param {any} product - A product from the recommendations payload.
   * @returns {HTMLLIElement}
   */
  #row(product) {
    const item = document.createElement('li');
    item.className = 'cart-recommendations__item';

    const media = document.createElement('span');
    media.className = 'cart-recommendations__media';
    if (product.featured_image) {
      const img = document.createElement('img');
      img.className = 'cart-recommendations__image';
      img.src = product.featured_image;
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.width = 28;
      img.height = 28;
      media.appendChild(img);
    }

    const name = document.createElement('span');
    name.className = 'cart-recommendations__name';
    name.textContent = product.title || '';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button-unstyled cart-recommendations__add';
    button.textContent = Theme.translations?.add ?? 'Add';
    button.dataset.variantId = String(product.variants?.[0]?.id ?? '');
    button.addEventListener('click', this.#add);

    item.append(media, name, button);
    return item;
  }

  /**
   * Adds the chosen product to the cart.
   * @param {MouseEvent & {currentTarget: HTMLButtonElement}} event - The click event.
   */
  #add = async (event) => {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const { status } = this.refs;
    button.disabled = true;

    try {
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');

      const sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
      });
      if (sectionIds.length) body.append('sections', sectionIds.join(','));

      const config = fetchConfig('javascript', { body });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const result = await response.json();

      if (result.status) {
        button.disabled = false;
        if (status) status.textContent = '';
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'cart-recommendations', {
          source: 'cart-recommendations',
          variantId,
          sections: result.sections,
        })
      );
    } catch (error) {
      button.disabled = false;
      if (status) status.textContent = '';
    }
  };
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
